import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronRight, 
  Building2, 
  User, 
  Mail, 
  Phone,
  Search,
  CheckCircle,
  XCircle,
  Edit2,
  Save,
  X
} from 'lucide-react';

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  user_type: string | null;
  fmcsa_verified: boolean | null;
  fmcsa_mc_number: string | null;
  fmcsa_dot_number: string | null;
  fmcsa_legal_name: string | null;
  onboarding_completed: boolean | null;
  is_enterprise: boolean | null;
  created_at: string | null;
}

interface Company {
  id: string;
  name: string;
  company_type: string;
  mc_number: string | null;
  dot_number: string | null;
  credit_score: number | null;
  verification_status: string | null;
  users: Profile[];
}

export default function AdminUsers() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [standaloneUsers, setStandaloneUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [standaloneExpanded, setStandaloneExpanded] = useState(false);
  const [editingCreditScore, setEditingCreditScore] = useState<string | null>(null);
  const [creditScoreValue, setCreditScoreValue] = useState<string>('');
  const [editingFmcsa, setEditingFmcsa] = useState<string | null>(null);
  const [fmcsaValues, setFmcsaValues] = useState<{ mc: string; dot: string }>({ mc: '', dot: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load companies with their users
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('*')
        .order('name');

      if (companiesError) throw companiesError;

      // Load company_users relationships
      const { data: companyUsersData, error: cuError } = await supabase
        .from('company_users')
        .select('company_id, user_id');

      if (cuError) throw cuError;

      // Load all profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Map users to companies
      const userCompanyMap = new Map<string, string[]>();
      companyUsersData?.forEach(cu => {
        if (!userCompanyMap.has(cu.company_id)) {
          userCompanyMap.set(cu.company_id, []);
        }
        userCompanyMap.get(cu.company_id)!.push(cu.user_id);
      });

      // Build companies with users
      const companiesWithUsers: Company[] = (companiesData || []).map(company => ({
        ...company,
        users: (profilesData || []).filter(profile => 
          userCompanyMap.get(company.id)?.includes(profile.id)
        )
      }));

      // Find standalone users (not in any company)
      const usersInCompanies = new Set(companyUsersData?.map(cu => cu.user_id) || []);
      const standalone = (profilesData || []).filter(profile => !usersInCompanies.has(profile.id));

      setCompanies(companiesWithUsers);
      setStandaloneUsers(standalone);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (companyId: string) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const handleSaveCreditScore = async (companyId: string) => {
    const score = parseInt(creditScoreValue);
    if (isNaN(score) || score < 0 || score > 850) {
      toast.error('Credit score must be between 0 and 850');
      return;
    }

    try {
      const { error } = await supabase
        .from('companies')
        .update({ credit_score: score })
        .eq('id', companyId);

      if (error) throw error;

      setCompanies(prev => prev.map(c => 
        c.id === companyId ? { ...c, credit_score: score } : c
      ));
      toast.success('Credit score updated');
      setEditingCreditScore(null);
    } catch (error) {
      console.error('Error updating credit score:', error);
      toast.error('Failed to update credit score');
    }
  };

  // Writes to another user's profile go through admin-only SECURITY DEFINER
  // RPCs; the profiles table itself still only lets a user edit their own row.
  const handleApproveFmcsa = async (userId: string) => {
    try {
      const { error } = await supabase.rpc('admin_set_user_fmcsa' as any, {
        p_user_id: userId,
        p_verified: true,
        p_mc_number: fmcsaValues.mc || null,
        p_dot_number: fmcsaValues.dot || null,
      });

      if (error) throw error;

      // Update local state
      setStandaloneUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, fmcsa_verified: true, fmcsa_mc_number: fmcsaValues.mc, fmcsa_dot_number: fmcsaValues.dot } : u
      ));
      setCompanies(prev => prev.map(c => ({
        ...c,
        users: c.users.map(u => 
          u.id === userId ? { ...u, fmcsa_verified: true, fmcsa_mc_number: fmcsaValues.mc, fmcsa_dot_number: fmcsaValues.dot } : u
        )
      })));

      toast.success('FMCSA approved');
      setEditingFmcsa(null);
    } catch (error) {
      console.error('Error approving FMCSA:', error);
      toast.error('Failed to approve FMCSA');
    }
  };

  const handleRevokeFmcsa = async (userId: string) => {
    if (!confirm('Are you sure you want to revoke FMCSA verification?')) return;

    try {
      const { error } = await supabase.rpc('admin_set_user_fmcsa' as any, {
        p_user_id: userId,
        p_verified: false,
      });

      if (error) throw error;

      setStandaloneUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, fmcsa_verified: false } : u
      ));
      setCompanies(prev => prev.map(c => ({
        ...c,
        users: c.users.map(u => 
          u.id === userId ? { ...u, fmcsa_verified: false } : u
        )
      })));

      toast.success('FMCSA verification revoked');
    } catch (error) {
      console.error('Error revoking FMCSA:', error);
      toast.error('Failed to revoke FMCSA');
    }
  };

  // Enterprise accounts get one rolled-up bid push instead of one per bid,
  // and no bid/quote email or SMS. Flipping this also starts (or stops) that
  // account's roll-up window server-side, so no past bids are ever alerted.
  const handleToggleEnterprise = async (userId: string, next: boolean) => {
    try {
      const { error } = await supabase.rpc('admin_set_user_enterprise' as any, {
        p_user_id: userId,
        p_enabled: next,
      });
      if (error) throw error;

      setStandaloneUsers(prev => prev.map(u => (u.id === userId ? { ...u, is_enterprise: next } : u)));
      setCompanies(prev => prev.map(c => ({
        ...c,
        users: c.users.map(u => (u.id === userId ? { ...u, is_enterprise: next } : u)),
      })));
      toast.success(next ? 'Enterprise account enabled (batched bid alerts)' : 'Enterprise account disabled (instant bid alerts)');
    } catch (error) {
      console.error('Error updating enterprise flag:', error);
      toast.error('Failed to update enterprise setting');
    }
  };


  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.mc_number?.includes(searchTerm) ||
    c.dot_number?.includes(searchTerm) ||
    c.users.some(u => 
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const filteredStandalone = standaloneUsers.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.fmcsa_mc_number?.includes(searchTerm) ||
    u.fmcsa_dot_number?.includes(searchTerm)
  );

  const UserRow = ({ user, indent = false }: { user: Profile; indent?: boolean }) => (
    <div className={`flex items-center justify-between py-3 px-4 ${indent ? 'pl-12 bg-[#0d1117]' : ''}`}>
      <div className="flex items-center gap-4">
        <div className="w-8 h-8 rounded-full bg-[#1e2530] flex items-center justify-center">
          <User className="w-4 h-4 text-[#6b7280]" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium">
              {user.first_name || user.last_name 
                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                : 'No name'}
            </span>
            {user.user_type && (
              <Badge variant="outline" className="text-xs capitalize">
                {user.user_type}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-[#6b7280] mt-0.5">
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {user.email}
            </span>
            {user.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {user.phone}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Enterprise account — batched bid alerts */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6b7280]">Enterprise</span>
          <Switch
            checked={!!user.is_enterprise}
            onCheckedChange={(v) => handleToggleEnterprise(user.id, v)}
          />
        </div>

        {/* FMCSA Status */}
        <div className="flex items-center gap-2">
          {editingFmcsa === user.id ? (
            <div className="flex items-center gap-2">
              <Input
                placeholder="MC #"
                value={fmcsaValues.mc}
                onChange={(e) => setFmcsaValues(v => ({ ...v, mc: e.target.value }))}
                className="w-24 h-7 text-xs bg-[#1e2530] border-[#2a3441]"
              />
              <Input
                placeholder="DOT #"
                value={fmcsaValues.dot}
                onChange={(e) => setFmcsaValues(v => ({ ...v, dot: e.target.value }))}
                className="w-24 h-7 text-xs bg-[#1e2530] border-[#2a3441]"
              />
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleApproveFmcsa(user.id)}>
                <Save className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingFmcsa(null)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <>
              {user.fmcsa_verified ? (
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  FMCSA Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[#6b7280]">
                  <XCircle className="w-3 h-3 mr-1" />
                  Not Verified
                </Badge>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setEditingFmcsa(user.id);
                  setFmcsaValues({ mc: user.fmcsa_mc_number || '', dot: user.fmcsa_dot_number || '' });
                }}
              >
                <Edit2 className="w-3 h-3" />
              </Button>
              {user.fmcsa_verified && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-red-400 hover:text-red-300"
                  onClick={() => handleRevokeFmcsa(user.id)}
                >
                  Revoke
                </Button>
              )}
            </>
          )}
        </div>

        {/* MC/DOT Numbers */}
        {(user.fmcsa_mc_number || user.fmcsa_dot_number) && (
          <div className="text-xs text-[#6b7280]">
            {user.fmcsa_mc_number && <span>MC: {user.fmcsa_mc_number}</span>}
            {user.fmcsa_mc_number && user.fmcsa_dot_number && <span className="mx-1">|</span>}
            {user.fmcsa_dot_number && <span>DOT: {user.fmcsa_dot_number}</span>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Users & Companies</h1>
          <p className="text-[#6b7280] mt-1">Manage users, credit scores, and FMCSA verification</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
            <Input
              placeholder="Search companies, users, MC/DOT numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#111820] border-[#1e2530] text-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#14b8a6]"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Companies */}
            {filteredCompanies.map((company) => (
              <Card key={company.id} className="bg-[#111820] border-[#1e2530] overflow-hidden">
                {/* Company Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1a2332] transition-colors"
                  onClick={() => toggleCompany(company.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#14b8a6]/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#14b8a6]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{company.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {company.company_type}
                        </Badge>
                        {company.verification_status === 'verified' && (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                            Verified
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-[#6b7280] mt-0.5">
                        {company.mc_number && <span>MC: {company.mc_number}</span>}
                        {company.mc_number && company.dot_number && <span className="mx-2">|</span>}
                        {company.dot_number && <span>DOT: {company.dot_number}</span>}
                        <span className="mx-2">|</span>
                        <span>{company.users.length} user{company.users.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Credit Score */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6b7280]">Credit Score:</span>
                      {editingCreditScore === company.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            max="850"
                            value={creditScoreValue}
                            onChange={(e) => setCreditScoreValue(e.target.value)}
                            className="w-20 h-7 text-xs bg-[#1e2530] border-[#2a3441]"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveCreditScore(company.id);
                            }}
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCreditScore(null);
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-white font-medium">
                            {company.credit_score ?? 'N/A'}
                          </span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCreditScore(company.id);
                              setCreditScoreValue(company.credit_score?.toString() || '');
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {expandedCompanies.has(company.id) ? (
                      <ChevronDown className="w-5 h-5 text-[#6b7280]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#6b7280]" />
                    )}
                  </div>
                </div>

                {/* Users List */}
                {expandedCompanies.has(company.id) && company.users.length > 0 && (
                  <div className="border-t border-[#1e2530]">
                    {company.users.map((user) => (
                      <div key={user.id} className="border-b border-[#1e2530] last:border-b-0">
                        <UserRow user={user} indent />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}

            {/* Standalone Users */}
            {filteredStandalone.length > 0 && (
              <Card className="bg-[#111820] border-[#1e2530] overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1a2332] transition-colors"
                  onClick={() => setStandaloneExpanded(!standaloneExpanded)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#6b7280]/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#6b7280]" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Users without Company</h3>
                      <p className="text-xs text-[#6b7280] mt-0.5">{filteredStandalone.length} user{filteredStandalone.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {standaloneExpanded ? (
                    <ChevronDown className="w-5 h-5 text-[#6b7280]" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-[#6b7280]" />
                  )}
                </div>
                {standaloneExpanded && (
                  <div className="border-t border-[#1e2530]">
                    {filteredStandalone.map((user) => (
                      <div key={user.id} className="border-b border-[#1e2530] last:border-b-0">
                        <UserRow user={user} />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {filteredCompanies.length === 0 && filteredStandalone.length === 0 && (
              <Card className="bg-[#111820] border-[#1e2530] p-12">
                <div className="text-center text-[#6b7280]">
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No results found</p>
                  <p className="text-sm mt-2">Try adjusting your search term</p>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
